import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { get, post, patch, del, API } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { fmtBRL, fmtPct, fmtDate } from '../lib/format';
import { Plus, Trash2, Users, DollarSign, TrendingUp, Percent, GraduationCap, Receipt, FileText, Layers, Filter, Pencil, Upload, FileDown, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import KpiCard from './widgets/KpiCard';

const CC_LABELS = {
  OPERACAO: 'Operação',
  PESSOAL: 'Pessoal',
  KIT_ALUNO: 'Kit do Aluno',
  EXPERIENCIAS: 'Experiências',
  EXTRAS: 'Extras',
};
const CC_COLORS = {
  OPERACAO: '#3B82F6',
  PESSOAL: '#8B5CF6',
  KIT_ALUNO: '#D4AF37',
  EXPERIENCIAS: '#10B981',
  EXTRAS: '#71717A',
};
const CC_LIST = ['OPERACAO', 'PESSOAL', 'KIT_ALUNO', 'EXPERIENCIAS', 'EXTRAS'];

const blankCost = () => ({
  description: '',
  course_cost_center: 'OPERACAO',
  cost_type: 'UNICO',
  unit_amount: 0,
  notes: '',
});

export default function CourseDetailDialog({ course, open, onClose, onChanged }) {
  const [dashboard, setDashboard] = useState(null);
  const [costForm, setCostForm] = useState(blankCost());
  const [submitting, setSubmitting] = useState(false);
  const [showCostForm, setShowCostForm] = useState(false);
  const [editingCostId, setEditingCostId] = useState(null);
  const [ccFilter, setCcFilter] = useState('all');
  // Estado do fluxo "Subir Receita" (upload em lote — Doc de Alteração nov/2026)
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { status, message, errors[] }

  const load = async () => {
    if (!course?.id) return;
    const d = await get(`/courses/${course.id}/dashboard`);
    setDashboard(d);
  };

  useEffect(() => {
    if (open && course?.id) {
      load();
      setCostForm(blankCost());
      setShowCostForm(false);
      setEditingCostId(null);
      setCcFilter('all');
      setUploadOpen(false);
      setUploadResult(null);
    }
  }, [open, course?.id]);

  const k = dashboard?.kpis || {};
  const clients = dashboard?.clients || [];
  const costs = dashboard?.costs || [];
  const inscritos = k.inscritos || 0;

  const filteredCosts = ccFilter === 'all' ? costs : costs.filter((c) => (c.course_cost_center || 'EXTRAS') === ccFilter);

  // Totais por centro de custo (cards de resumo)
  const ccTotals = useMemo(() => {
    const t = { OPERACAO: 0, PESSOAL: 0, KIT_ALUNO: 0, EXPERIENCIAS: 0, EXTRAS: 0 };
    (dashboard?.cost_centers || []).forEach((cc) => { if (t[cc.center] !== undefined) t[cc.center] = cc.amount; });
    return t;
  }, [dashboard]);

  const previewCostTotal = (cf) => {
    const amt = parseFloat(String(cf.unit_amount).replace(',', '.')) || 0;
    return cf.cost_type === 'POR_ALUNO' ? amt * (inscritos || 0) : amt;
  };

  const downloadTemplate = () => {
    // Download direto pela URL (browser abre com auth cookie)
    const url = `${API}/courses/${course.id}/nf-template.xlsx`;
    // Fallback via axios blob para garantir header Authorization
    axios
      .get(url, { responseType: 'blob', withCredentials: true })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const disp = res.headers['content-disposition'] || '';
        const match = /filename="?([^"]+)"?/i.exec(disp);
        const filename = match ? match[1] : `template-subir-receita_${course.id}.xlsx`;
        const objUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(objUrl);
        toast.success('Template baixado — preencha e reenvie aqui');
      })
      .catch(() => toast.error('Erro ao baixar template'));
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';  // permite re-selecionar o mesmo arquivo
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Arquivo precisa ser Excel (.xlsx)');
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(
        `${API}/courses/${course.id}/nf-upload`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setUploadResult(res.data);
      toast.success(res.data.message || 'Importação concluída');
      await load();
      onChanged?.();
    } catch (err) {
      const body = err.response?.data;
      if (body && body.status === 'BLOCKED') {
        // Erros validados — mostra lista discriminada no dialog
        setUploadResult(body);
        toast.error(body.message || 'Importação bloqueada por erros de validação');
      } else {
        setUploadResult({
          status: 'ERROR',
          message: body?.detail || body?.message || err.message || 'Erro ao enviar planilha',
        });
        toast.error(body?.detail || 'Erro no upload');
      }
    } finally {
      setUploading(false);
    }
  };
  // ==================================================================

  const removeClient = async (clientId) => {
    if (!window.confirm('Remover esta NF?')) return;
    await del(`/courses/${course.id}/clients/${clientId}`);
    toast.success('Removido');
    await load();
    onChanged?.();
  };

  const startEditCost = (c) => {
    setEditingCostId(c.id);
    setCostForm({
      description: c.description || '',
      course_cost_center: c.course_cost_center || 'OPERACAO',
      cost_type: c.cost_type || 'UNICO',
      unit_amount: c.unit_amount ?? 0,
      notes: c.notes || '',
    });
    setShowCostForm(true);
  };

  const cancelCostForm = () => {
    setEditingCostId(null);
    setCostForm(blankCost());
    setShowCostForm(false);
  };

  const submitCost = async () => {
    if (!costForm.description.trim()) return toast.error('Descrição obrigatória');
    const unit = parseFloat(String(costForm.unit_amount).replace(',', '.'));
    if (!unit || unit <= 0) return toast.error('Valor inválido');
    if (!costForm.course_cost_center) return toast.error('Selecione o centro de custo');
    setSubmitting(true);
    try {
      const payload = {
        description: costForm.description.trim(),
        course_cost_center: costForm.course_cost_center,
        cost_type: costForm.cost_type,
        unit_amount: unit,
        notes: costForm.notes || null,
      };
      if (editingCostId) {
        await patch(`/courses/${course.id}/costs/${editingCostId}`, payload);
        toast.success('Custo atualizado');
      } else {
        await post(`/courses/${course.id}/costs`, payload);
        toast.success(costForm.cost_type === 'POR_ALUNO' ? `Custo POR ALUNO lançado (× ${inscritos} inscritos)` : 'Custo único lançado');
      }
      setEditingCostId(null);
      setCostForm({ ...blankCost(), course_cost_center: costForm.course_cost_center });
      setShowCostForm(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(editingCostId ? 'Erro ao atualizar custo' : 'Erro ao lançar custo');
    } finally {
      setSubmitting(false);
    }
  };

  const removeCost = async (costId) => {
    if (!window.confirm('Remover este custo?')) return;
    await del(`/courses/${course.id}/costs/${costId}`);
    toast.success('Removido');
    await load();
    onChanged?.();
  };

  if (!course) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-7xl max-h-[92vh] overflow-y-auto" data-testid="dialog-course-detail">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-md bg-amber-950/30 border border-amber-700/40 flex items-center justify-center shrink-0">
              <GraduationCap size={22} className="text-amber-400" />
            </div>
            <div>
              <DialogTitle className="font-display text-xl">{course.name}</DialogTitle>
              <DialogDescription className="text-xs text-zinc-500 mt-1 space-x-2">
                {course.instructor && <span>👤 {course.instructor}</span>}
                {course.location && <span>📍 {course.location}</span>}
                {course.start_date && <span>📅 {fmtDate(course.start_date)}</span>}
                <span className="text-amber-400">· controle paralelo (não impacta fluxo de caixa)</span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* KPIs em 2 linhas de 4 colunas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <KpiCard label="Receita" value={k.receita || 0} accent="#D4AF37" icon={DollarSign} testid="course-detail-kpi-receita" highlight />
          <KpiCard label="Custo" value={k.custos || 0} accent="#EF4444" icon={Receipt} testid="course-detail-kpi-custo" />
          <KpiCard label="Lucro" value={k.lucro || 0} accent={(k.lucro || 0) >= 0 ? '#10B981' : '#EF4444'} icon={TrendingUp} testid="course-detail-kpi-lucro" />
          <KpiCard label="Ticket Médio" value={k.ticket_medio || 0} accent="#F59E0B" icon={DollarSign} testid="course-detail-kpi-ticket" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <KpiCard label="Inscritos" value={inscritos} format="number" accent="#8B5CF6" icon={Users} testid="course-detail-kpi-inscritos" />
          <KpiCard label="Margem de Custo" value={k.margem_custo || 0} format="percent" accent="#EF4444" icon={Percent} testid="course-detail-kpi-margem-custo" />
          <KpiCard label="Margem de Lucro" value={k.margem || 0} format="percent" accent={(k.margem || 0) >= 0 ? '#10B981' : '#EF4444'} icon={Percent} testid="course-detail-kpi-margem-lucro" />
          <KpiCard label="Custo por Aluno" value={k.custo_por_aluno || 0} accent="#F59E0B" icon={Users} testid="course-detail-kpi-custo-aluno" />
        </div>

        {/* Duas colunas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* COLUNA ESQUERDA: NF EMITIDAS */}
          <div className="border border-zinc-800 rounded-md bg-zinc-900/30">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <FileText size={15} className="text-amber-400" />
                <span className="font-display text-zinc-100">NF Emitidas</span>
                <span className="text-[11px] text-zinc-500">({clients.length})</span>
              </div>
              <Button
                onClick={() => { setUploadOpen(true); setUploadResult(null); }}
                size="sm"
                className="btn-gold gap-1"
                data-testid="btn-subir-receita"
              >
                <Upload size={13} /> Subir Receita
              </Button>
            </div>

            <div className="overflow-x-auto" style={{ maxHeight: 460 }}>
              <table className="w-full ceap-table" data-testid="course-clients-table">
                <thead className="sticky top-0 bg-zinc-950 z-10">
                  <tr>
                    <th>Município / Órgão</th>
                    <th className="text-center">Inscritos</th>
                    <th className="text-right">Valor</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-zinc-500 py-8 text-sm">Nenhuma NF emitida ainda</td></tr>
                  )}
                  {clients.map((c) => (
                    <tr key={c.id} data-testid={`client-row-${c.id}`}>
                      <td>
                        {c.municipality ? (
                          <>
                            <div className="text-zinc-100 font-medium">
                              {c.municipality}
                              {c.uf && <span className="ml-1 text-[10px] text-zinc-500 font-mono">/ {c.uf}</span>}
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-0.5">
                              {c.organ_name}
                              <span className="mx-1 text-zinc-700">·</span>
                              NF: {c.invoice_date ? fmtDate(c.invoice_date) : '-'}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-zinc-100 font-medium">{c.organ_name}</div>
                            <div className="text-[10px] text-zinc-500 mt-0.5">
                              NF: {c.invoice_date ? fmtDate(c.invoice_date) : '-'}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="text-center font-mono">{c.num_students}</td>
                      <td className="text-right font-mono emerald-text">{fmtBRL(c.total_revenue)}</td>
                      <td className="text-right">
                        <button onClick={() => removeClient(c.id)} className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400" data-testid={`btn-remove-client-${c.id}`} title="Excluir NF">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {clients.length > 0 && (
                    <tr className="bg-zinc-900/40 border-t-2 border-amber-700/40">
                      <td className="text-zinc-100 font-medium">TOTAL</td>
                      <td className="text-center font-mono font-bold">{inscritos}</td>
                      <td className="text-right font-mono font-bold emerald-text">{fmtBRL(k.receita || 0)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* COLUNA DIREITA: CENTROS DE CUSTO */}
          <div className="border border-zinc-800 rounded-md bg-zinc-900/30">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Layers size={15} className="text-amber-400" />
                <span className="font-display text-zinc-100">Centro de Custo</span>
                <span className="text-[11px] text-zinc-500">({filteredCosts.length} lanç.)</span>
              </div>
              <div className="flex items-center gap-2">
                <Filter size={11} className="text-zinc-500" />
                <Select value={ccFilter} onValueChange={setCcFilter}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 h-7 text-xs w-44" data-testid="cc-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="all">Todos os centros</SelectItem>
                    {CC_LIST.map((c) => <SelectItem key={c} value={c}>{CC_LABELS[c]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={() => (showCostForm ? cancelCostForm() : setShowCostForm(true))} size="sm" className="btn-gold gap-1" data-testid="btn-toggle-cost-form">
                  <Plus size={13} /> {showCostForm ? 'Cancelar' : 'Novo Custo'}
                </Button>
              </div>
            </div>

            {/* Resumo dos 5 centros */}
            <div className="grid grid-cols-5 gap-1.5 px-3 py-2.5 border-b border-zinc-800 bg-zinc-950/30">
              {CC_LIST.map((cc) => (
                <button
                  key={cc}
                  onClick={() => setCcFilter(ccFilter === cc ? 'all' : cc)}
                  className={`text-center px-1.5 py-1.5 rounded transition-all border ${ccFilter === cc ? 'border-amber-500/60 bg-amber-950/30' : 'border-zinc-800/40 bg-zinc-950/30 hover:bg-zinc-900/50'}`}
                  data-testid={`cc-card-${cc}`}
                  aria-pressed={ccFilter === cc}
                >
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: CC_COLORS[cc] }} />
                    <span className="text-[9px] uppercase tracking-wider text-zinc-400 truncate">{CC_LABELS[cc]}</span>
                  </div>
                  <div className="font-mono text-[11px] text-zinc-100">{fmtBRL(ccTotals[cc])}</div>
                </button>
              ))}
            </div>

            {showCostForm && (
              <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-950/50 space-y-2">
                {editingCostId && (
                  <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-950/25 border border-amber-700/40 rounded text-[11px] text-amber-200">
                    <Pencil size={11} className="text-amber-300" />
                    <span>Editando custo existente — as alterações salvam ao clicar em <strong>Atualizar Custo</strong>.</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <Label className="text-zinc-400 text-[10px] uppercase tracking-wider">Descrição *</Label>
                    <Input value={costForm.description} onChange={(e) => setCostForm({ ...costForm, description: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-0.5 h-8 text-sm" placeholder="Ex: Palestrante, Coffee Break..." data-testid="cost-description" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-[10px] uppercase tracking-wider">Centro de Custo *</Label>
                    <Select value={costForm.course_cost_center} onValueChange={(v) => setCostForm({ ...costForm, course_cost_center: v })}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-0.5 h-8 text-sm" data-testid="cost-course-cc"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                        {CC_LIST.map((c) => <SelectItem key={c} value={c}>{CC_LABELS[c]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-[10px] uppercase tracking-wider">Tipo de Custo *</Label>
                    <Select value={costForm.cost_type} onValueChange={(v) => setCostForm({ ...costForm, cost_type: v })}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-0.5 h-8 text-sm" data-testid="cost-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                        <SelectItem value="UNICO">Único</SelectItem>
                        <SelectItem value="POR_ALUNO">Por Aluno (× {inscritos || 0})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-zinc-400 text-[10px] uppercase tracking-wider">
                      {costForm.cost_type === 'POR_ALUNO' ? 'R$ por Aluno *' : 'Valor Total (R$) *'}
                    </Label>
                    <Input value={costForm.unit_amount} onChange={(e) => setCostForm({ ...costForm, unit_amount: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-0.5 h-8 text-sm font-mono" placeholder="0,00" data-testid="cost-unit-amount" />
                    <div className="text-[10px] text-zinc-500 mt-1">
                      {costForm.cost_type === 'POR_ALUNO' ? (
                        <>Total = R$ {String(costForm.unit_amount || '0').replace('.', ',')} × {inscritos || 0} inscritos = <strong className="red-text">{fmtBRL(previewCostTotal(costForm))}</strong></>
                      ) : (
                        <>Total: <strong className="red-text">{fmtBRL(previewCostTotal(costForm))}</strong></>
                      )}
                    </div>
                  </div>
                </div>
                <Button onClick={submitCost} disabled={submitting} className="btn-gold w-full gap-1.5 mt-1" size="sm" data-testid="btn-add-cost">
                  {editingCostId ? (
                    <><Pencil size={13} /> Atualizar Custo</>
                  ) : (
                    <><Plus size={13} /> Lançar Custo</>
                  )}
                </Button>
              </div>
            )}

            <div className="overflow-x-auto" style={{ maxHeight: showCostForm ? 280 : 460 }}>
              <table className="w-full ceap-table" data-testid="course-costs-table">
                <thead className="sticky top-0 bg-zinc-950 z-10">
                  <tr>
                    <th>Descrição</th>
                    <th>Centro</th>
                    <th>Tipo</th>
                    <th className="text-right">Valor</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCosts.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-zinc-500 py-8 text-sm">Nenhum custo {ccFilter !== 'all' ? `em ${CC_LABELS[ccFilter]}` : 'lançado'}</td></tr>
                  )}
                  {filteredCosts.map((c) => {
                    const cc = c.course_cost_center || 'EXTRAS';
                    return (
                      <tr key={c.id} data-testid={`cost-row-${c.id}`}>
                        <td>
                          <div className="text-zinc-100">{c.description}</div>
                          <div className="text-[10px] text-zinc-500">
                            {c.cost_type === 'POR_ALUNO' ? `${fmtBRL(c.unit_amount)} × ${c.inscritos_snapshot} alunos` : 'Valor único'}
                          </div>
                        </td>
                        <td>
                          <span className="inline-flex items-center gap-1 text-[10px]">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: CC_COLORS[cc] || '#71717A' }} />
                            {CC_LABELS[cc] || cc}
                          </span>
                        </td>
                        <td className="text-[10px] text-zinc-400">{c.cost_type === 'POR_ALUNO' ? 'Por aluno' : 'Único'}</td>
                        <td className="text-right font-mono red-text">{fmtBRL(c.total_amount)}</td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => startEditCost(c)}
                              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"
                              title="Editar custo"
                              data-testid={`btn-edit-cost-${c.id}`}
                            >
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => removeCost(c.id)} className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400" data-testid={`btn-remove-cost-${c.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredCosts.length > 0 && (
                    <tr className="bg-zinc-900/40 border-t-2 border-amber-700/40">
                      <td colSpan={3} className="text-zinc-100 font-medium">TOTAL</td>
                      <td className="text-right font-mono font-bold red-text">
                        {fmtBRL(filteredCosts.reduce((s, c) => s + Number(c.total_amount || 0), 0))}
                      </td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Dialog: Subir Receita — Upload em lote (Doc de Alteração nov/2026) */}
      <Dialog open={uploadOpen} onOpenChange={(v) => !v && setUploadOpen(false)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="dialog-subir-receita">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Upload size={18} className="text-[#D4AF37]" /> Subir Receita — {course?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Faça o upload de uma planilha Excel para cadastrar múltiplas NFs de uma só vez neste curso.
              As NFs entram como <strong>PENDENTES</strong> em Cobrança — o pagamento é gerenciado lá.
            </DialogDescription>
          </DialogHeader>

          {/* Passo 1: Download template */}
          <div className="border border-zinc-800 rounded-md p-4 bg-zinc-900/40">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-950/40 border border-amber-700/40 text-amber-300 text-[11px] font-semibold shrink-0">
                1
              </div>
              <div className="flex-1">
                <div className="text-[13px] font-medium text-zinc-100">Baixar o template padrão</div>
                <div className="text-[11.5px] text-zinc-400 mt-0.5">
                  Um arquivo .xlsx com as 6 colunas obrigatórias, já formatado com um exemplo.
                </div>
              </div>
              <Button
                onClick={downloadTemplate}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 gap-1.5"
                data-testid="btn-download-template"
              >
                <FileDown size={13} /> Baixar Template
              </Button>
            </div>
          </div>

          {/* Passo 2: Upload preenchido */}
          <div className="border border-zinc-800 rounded-md p-4 bg-zinc-900/40">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-950/40 border border-amber-700/40 text-amber-300 text-[11px] font-semibold shrink-0">
                2
              </div>
              <div className="flex-1">
                <div className="text-[13px] font-medium text-zinc-100">Subir o arquivo preenchido</div>
                <div className="text-[11.5px] text-zinc-400 mt-0.5">
                  Só serão importadas se <strong>todas</strong> as linhas estiverem válidas.
                  Se qualquer linha estiver com erro, nenhuma NF entrará — você recebe a lista de correções.
                </div>
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx,.xlsm"
                  onChange={handleFileSelected}
                  disabled={uploading}
                  className="hidden"
                  data-testid="input-upload-file"
                />
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium transition-all ${
                    uploading
                      ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                      : 'btn-gold cursor-pointer'
                  }`}
                >
                  {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {uploading ? 'Enviando...' : 'Escolher Arquivo'}
                </span>
              </label>
            </div>
          </div>

          {/* Colunas obrigatórias */}
          <div className="border border-zinc-800 rounded-md p-3 bg-zinc-950/70">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Colunas obrigatórias (nesta ordem)</div>
            <div className="text-[11.5px] text-zinc-300 font-mono">
              Órgão / Cliente <span className="text-zinc-600 mx-1">|</span>
              Município <span className="text-zinc-600 mx-1">|</span>
              UF <span className="text-zinc-600 mx-1">|</span>
              N° de inscritos <span className="text-zinc-600 mx-1">|</span>
              Valor total da NF <span className="text-zinc-600 mx-1">|</span>
              Data de início do curso
            </div>
            <div className="text-[10.5px] text-zinc-500 mt-2">
              <strong>Formato:</strong> UF = 2 letras (SP, RJ...) · Inscritos = inteiro · Valor = número/R$ · Data = DD/MM/AAAA
            </div>
          </div>

          {/* Resultado do upload */}
          {uploadResult && uploadResult.status === 'SUCCESS' && (
            <div className="border border-emerald-700/40 rounded-md p-3 bg-emerald-950/20" data-testid="upload-result-success">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[13px] font-medium text-emerald-100">
                    ✓ {uploadResult.total_success} NF(s) importada(s) com sucesso
                  </div>
                  <div className="text-[11.5px] text-emerald-200/70 mt-0.5">
                    Elas já aparecem na lista abaixo e podem ser gerenciadas em Cobrança.
                  </div>
                </div>
              </div>
            </div>
          )}

          {uploadResult && uploadResult.status === 'BLOCKED' && (
            <div className="border border-red-700/40 rounded-md p-3 bg-red-950/20" data-testid="upload-result-blocked">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[13px] font-medium text-red-100">
                    Importação bloqueada — {uploadResult.total_errors} linha(s) com erro
                  </div>
                  <div className="text-[11.5px] text-red-200/70 mt-0.5">
                    Nenhuma NF foi importada. Corrija os erros abaixo, salve a planilha e reenvie.
                  </div>
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto border border-red-800/40 rounded bg-zinc-950/50" data-testid="upload-errors-list">
                <table className="w-full text-[11px]">
                  <thead className="bg-red-950/40 text-red-200 text-left">
                    <tr>
                      <th className="px-2 py-1.5 w-16">Linha</th>
                      <th className="px-2 py-1.5">Erro(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(uploadResult.errors || []).map((e, i) => (
                      <tr key={i} className="border-t border-red-950/40" data-testid={`upload-error-row-${i}`}>
                        <td className="px-2 py-1.5 text-red-300 font-mono">{e.row}</td>
                        <td className="px-2 py-1.5 text-red-100">
                          {(e.errors || []).map((msg, j) => (
                            <div key={j}>• {msg}</div>
                          ))}
                          {e.raw && (
                            <div className="text-[10px] text-red-300/60 mt-1 font-mono truncate">
                              {e.raw.filter(Boolean).join(' | ')}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {uploadResult && uploadResult.status === 'ERROR' && (
            <div className="border border-red-700/40 rounded-md p-3 bg-red-950/20" data-testid="upload-result-error">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
                <div className="text-[13px] text-red-100">{uploadResult.message}</div>
              </div>
            </div>
          )}

          <div className="flex justify-end mt-1">
            <Button
              onClick={() => setUploadOpen(false)}
              variant="ghost"
              className="text-zinc-400 hover:bg-zinc-900"
              size="sm"
              data-testid="btn-close-upload"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
